// Ocean Insight Pro Mobile - Oceanographic Redux Slice

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { OceanographicData, GeoCoordinate } from '../types';
import { OceanographicDataService } from '../services/oceanographicDataService';
import { logger } from '../utils/logger';

// Define the oceanographic state interface
interface OceanographicState {
  data: OceanographicData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

// Initial state
const initialState: OceanographicState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunks for real API calls
export const fetchOceanographicData = createAsyncThunk(
  'oceanographic/fetchData',
  async (params: { location: GeoCoordinate; gridSize?: number }) => {
    logger.info('Fetching real oceanographic data', {
      location: params.location,
      gridSize: params.gridSize,
    });

    const data = await OceanographicDataService.getOceanographicData(
      params.location,
      params.gridSize ?? 0.25
    );

    return data;
  }
);

export const refreshOceanographicData = createAsyncThunk(
  'oceanographic/refreshData',
  async (params: { location: GeoCoordinate; gridSize?: number }) => {
    logger.info('Refreshing oceanographic data', { location: params.location });

    const data = await OceanographicDataService.getOceanographicData(
      params.location,
      params.gridSize ?? 0.25
    );

    return data;
  }
);

// Oceanographic slice
const oceanographicSlice = createSlice({
  name: 'oceanographic',
  initialState,
  reducers: {
    clearOceanographicData: (state) => {
      state.data = null;
      state.error = null;
    },
    setOceanographicError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch oceanographic data
    builder
      .addCase(fetchOceanographicData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOceanographicData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchOceanographicData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch oceanographic data';
      });

    // Refresh oceanographic data
    builder
      .addCase(refreshOceanographicData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshOceanographicData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(refreshOceanographicData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to refresh oceanographic data';
      });
  },
});

// Export actions
export const { clearOceanographicData, setOceanographicError } = oceanographicSlice.actions;

// Export reducer
export default oceanographicSlice.reducer;
